import React, { useState } from 'react';
import { T, SPARKY_SERVER_URL } from '../shared/constants';

// Shared pieces of every NAPLAN item renderer (moved verbatim from MCQRenderer.jsx).

// ── Pipe-delimited table detection ──────────────────────────────────
// A run of 2+ consecutive lines that each contain "|" is a data table
// embedded in the question text (frequency tables, stem-and-leaf plots).
// Everything else renders exactly as plain question text, unchanged.

function isSeparatorRow(cells) {
  return cells.length > 0 && cells.every(c => /^:?-{2,}:?$/.test(c.trim()));
}

function trimEdgeEmptyCells(cells) {
  let c = cells;
  if (c.length > 1 && c[0].trim() === '') c = c.slice(1);
  if (c.length > 1 && c[c.length - 1].trim() === '') c = c.slice(0, -1);
  return c;
}

function parseTableBlock(lines) {
  const rows = lines.map(l => trimEdgeEmptyCells(l.split('|')).map(c => c.trim()));
  const headers = rows[0];
  let dataRows = rows.slice(1);
  // A markdown separator row (|---|---|) is a header divider, not data.
  if (dataRows.length && isSeparatorRow(dataRows[0])) dataRows = dataRows.slice(1);
  if (headers.length < 2 && dataRows.every(r => r.length < 2)) return null;
  return { headers, rows: dataRows };
}

function splitQuestionIntoBlocks(text) {
  if (!text) return [];
  const lines = text.split('\n');
  const blocks = [];
  let textBuf = [];
  const flushText = () => {
    const value = textBuf.join('\n').trim();
    if (value) blocks.push({ type: 'text', value });
    textBuf = [];
  };
  let i = 0;
  while (i < lines.length) {
    if (lines[i].includes('|')) {
      let j = i;
      while (j < lines.length && lines[j].includes('|')) j++;
      const run = lines.slice(i, j);
      if (run.length >= 2) {
        const table = parseTableBlock(run);
        if (table) {
          flushText();
          blocks.push({ type: 'table', ...table });
          i = j;
          continue;
        }
      }
    }
    textBuf.push(lines[i]);
    i++;
  }
  flushText();
  return blocks;
}

function QuestionDataTable({ headers, rows }) {
  return (
    <div style={{ overflowX: 'auto', margin: '14px 0', borderRadius: 10, border: `1px solid ${T.border}` }}>
      <table style={{
        borderCollapse: 'collapse', width: '100%', minWidth: 'max-content',
        fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 15,
        fontVariantNumeric: 'tabular-nums',
      }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{
                textAlign: 'left', padding: '10px 16px', background: T.surface,
                color: T.text, fontWeight: 700, borderBottom: `2px solid ${T.border}`,
                whiteSpace: 'nowrap',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 1 ? T.surface + '80' : 'transparent' }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{
                  padding: '9px 16px', color: T.text, borderBottom: `1px solid ${T.border}`,
                  whiteSpace: 'nowrap',
                }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function QuestionBody({ text }) {
  const blocks = splitQuestionIntoBlocks(text);
  return (
    <>
      {blocks.map((block, i) => block.type === 'table'
        ? <QuestionDataTable key={i} headers={block.headers} rows={block.rows} />
        : <p key={i} style={{ fontSize: 17, lineHeight: 1.7, color: T.text, fontWeight: 500, margin: i === 0 ? 0 : '14px 0 0' }}>{block.value}</p>
      )}
    </>
  );
}

export function ReportQuestionButton({ question }) {
  const [status, setStatus] = useState("idle");
  const [reason, setReason] = useState("");
  const [note, setNote]     = useState("");

  const REASONS = [
    "Wrong answer marked",
    "Question is unclear",
    "Explanation is incorrect",
    "Typo or spelling error",
    "Question too easy / too hard",
    "Other",
  ];

  async function submitReport() {
    if (!reason) return;
    setStatus("sending");
    const report = {
      id: question.id,
      question: question.q,
      reason,
      note,
      timestamp: new Date().toISOString(),
      yearLevel: question.year?.[0] || "unknown",
      topic: question.topic,
    };
    let existing;
    try { const _r = JSON.parse(localStorage.getItem("lumio_reports") || "[]"); existing = Array.isArray(_r) ? _r : []; } catch { existing = []; }
    existing.push(report);
    localStorage.setItem("lumio_reports", JSON.stringify(existing));
    try {
      await fetch(`${SPARKY_SERVER_URL}/api/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(report),
      });
    } catch(e) { /* silent — saved locally */ }
    setStatus("sent");
    setTimeout(() => setStatus("idle"), 2500);
  }

  if (status === "sent") return (
    <div style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 10px", background:`${T.green}15`, border:`1px solid ${T.green}44`, borderRadius:99, fontSize:12, color:T.green, fontWeight:700 }}>
      ✓ Thanks for the report
    </div>
  );

  return (
    <div style={{ position:"relative" }}>
      <button
        onClick={() => setStatus(status === "open" ? "idle" : "open")}
        title="Report an issue with this question"
        style={{ background: status === "open" ? `${T.coral}15` : "transparent", border:`1px solid ${status === "open" ? T.coral : T.border}`, borderRadius:99, padding:"3px 10px", fontSize:12, fontWeight:700, color: status === "open" ? T.coral : T.textDim, cursor:"pointer", display:"flex", alignItems:"center", gap:5, transition:"all .15s" }}>
        🚩 {status === "open" ? "Close" : "Report"}
      </button>
      {status === "open" && (
        <div style={{ position:"absolute", top:"calc(100% + 8px)", right:0, width:280, background:T.surface, border:`1px solid ${T.border}`, borderRadius:14, padding:16, zIndex:100, boxShadow:"0 8px 32px rgba(0,0,0,0.12)", animation:"fadeUp 0.2s ease" }}>
          <p style={{ fontSize:13, fontWeight:800, color:T.text, margin:"0 0 12px" }}>Report an issue</p>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:12 }}>
            {REASONS.map(r => (
              <button key={r} onClick={() => setReason(r)}
                style={{ padding:"5px 10px", borderRadius:99, border:`1.5px solid ${reason===r ? T.accent : T.border}`, background: reason===r ? `${T.accent}15` : T.bg, color: reason===r ? T.accent : T.textMid, fontSize:12, fontWeight:700, cursor:"pointer", transition:"all .15s" }}>
                {r}
              </button>
            ))}
          </div>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Add details (optional)"
            rows={2}
            style={{ width:"100%", padding:"8px 10px", borderRadius:8, border:`1.5px solid ${T.border}`, background:T.bg, color:T.text, fontSize:12, fontFamily:"'Plus Jakarta Sans',sans-serif", resize:"none", outline:"none", marginBottom:10, boxSizing:"border-box" }}
          />
          <button
            onClick={submitReport}
            disabled={!reason || status === "sending"}
            style={{ width:"100%", padding:"9px", borderRadius:9, border:"none", background: reason ? T.accent : T.border, color: reason ? "white" : T.textMid, fontSize:13, fontWeight:700, cursor: reason ? "pointer" : "not-allowed", fontFamily:"'Plus Jakarta Sans',sans-serif", transition:"all .15s" }}>
            {status === "sending" ? "Sending..." : "Submit Report"}
          </button>
        </div>
      )}
    </div>
  );
}

